import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import window from 'window';
import log from 'loglevel';
import useGeolocation, {
  GeolocationErrorType,
  GeolocationError,
} from 'hooks/useGeolocation';

import {
  setClientVariable as setCV,
  mergeApplication,
} from 'state/creditApplication';
import catchOnly from 'utils/catchOnly';
import useStoreAttributionRequest, {
  AddressObject,
  StoreAttributionError,
  StoreAttributionResult,
} from './requests/storeAttributionRequest';
import { NonClientError } from './requests/fetchUtils';
import useFeature from './useFeature';

import useLegacyStoreAttribution from './useLegacyStoreAttribution';
import { selectTenant } from 'state/tenant';

/**
 * Convert number to a string with a length limit.
 */
const numToString = (n: number, length: number) =>
  n
    .toFixed(length)
    .toString()
    .substr(0, length);

/**
 * Position string for clientVariable1
 */
const positionForCV1 = ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}) => {
  return [numToString(latitude, 12), numToString(longitude, 12)].join(',');
};

/**
 * Map Geolocation error to indicator for clientVariable3
 */
const reasonMap = {
  [GeolocationErrorType.Denied]: 'D',
  [GeolocationErrorType.Unavailable]: 'U',
  [GeolocationErrorType.Timeout]: 'T',
  [GeolocationErrorType.Unsupported]: 'N',
};

/**
 * Attempt to locate the user (by geolocation or home address) and attribute
 * the nearest store.
 *
 * Set the clientVariable1 and clientVariable3 accordingly.
 */
const useLocateAndAttribute = () => {
  const [shouldGeolocate, requestGeolocation] = useGeolocation();
  const dispatch = useDispatch();
  const [attributeStore] = useStoreAttributionRequest();
  const tenant = useSelector(selectTenant);

  const [
    legacyAttributeStore,
    shouldUseLegacyAttributeStore,
  ] = useLegacyStoreAttribution();

  const { isEnabled } = useFeature();

  const geolocate = useCallback(async () => {
    let position = null;
    try {
      position = await requestGeolocation();
      dispatch(setCV(3, 'A', 16));
    } catch (e) {
      if (catchOnly(e, GeolocationError)) {
        if (e instanceof GeolocationError) {
          dispatch(setCV(3, reasonMap[e.type], 16));
        }
        log.error(e.name, e);
      }
    }
    return position;
  }, [dispatch, requestGeolocation]);

  const attribute = useCallback(
    async (address, position) => {
      if (position) {
        dispatch(setCV(1, positionForCV1(position), 0));
      }
      try {
        let storeWithDistance: StoreAttributionResult;
        if (shouldUseLegacyAttributeStore) {
          const result = await legacyAttributeStore(address, position);
          if (!result) {
            throw new StoreAttributionError();
          }
          storeWithDistance = result;
        } else {
          storeWithDistance = await attributeStore(address, position);
        }
        const { storeNumber, distance } = storeWithDistance;

        dispatch(mergeApplication({ storeNumber: storeNumber }));
        dispatch(setCV(3, storeNumber, 17));

        if (distance) {
          const dist = distance
            .toFixed(0)
            .toString()
            .padStart(3, '0');
          dispatch(setCV(3, dist, 22));
        }
      } catch (e) {
        if (catchOnly(e, NonClientError, StoreAttributionError)) {
          console.warn('Store attribution request failed');
          return;
        } else {
          throw e;
        }
      }
    },
    [
      dispatch,
      shouldUseLegacyAttributeStore,
      legacyAttributeStore,
      attributeStore,
    ],
  );

  const locateAndAttribute = useCallback(
    async (values: Partial<AddressObject>) => {
      if (!isEnabled('storeAttribution') && !tenant.geolocationAttribution) {
        return;
      }

      // In Samsung Internet, the location prompt is not blocking.
      // When a user is focused on the last element of the form, they may press
      // on submit button on the virtual keyboard. This doesn't blur the input,
      // and the keyboard remains open and cover up the location prompt.
      if (window.document && 'activeElement' in window.document) {
        (window.document.activeElement as HTMLElement).blur();
      }

      let position = null;

      if (shouldGeolocate) {
        position = await geolocate();
      }

      await attribute(values, position);
    },
    [
      isEnabled,
      tenant.geolocationAttribution,
      shouldGeolocate,
      attribute,
      geolocate,
    ],
  );

  return locateAndAttribute;
};

export default useLocateAndAttribute;
